import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { useState, FC } from 'react'
import { EffectCoverflow } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useStarAnim, unlerp, DustedText, LottieFromFrame } from '@shared'

import profileStripes from '../../assets/profileStripes.json'
import styles from './ItemsRating.module.scss'
import { SlideItem } from './SlideItem'
import { getDiffFromCurrentSlide } from './utils'
import { InventoryCard } from '@store'

type Props = {
  items: InventoryCard[]
  rating: number
}

export const ItemsRating: FC<Props> = ({ items, rating }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const { starAnim } = useStarAnim()

  return (
    <div className={styles.container}>
      <LottieFromFrame
        data={profileStripes}
        width={140}
        height={140}
        className={classNames(styles.stripesAnim, styles.stripesAnimLeft)}
        frame={15}
      />
      <DotLottieWorkerReact
        data={profileStripes}
        autoplay
        loop
        width={140}
        height={140}
        className={classNames(styles.stripesAnim, styles.stripesAnimRight)}
      />
      {starAnim && (
        <>
          <LottieFromFrame
            data={starAnim}
            width={126}
            height={126}
            className={classNames(styles.starAnim, styles.starAnimLeft)}
            frame={10}
          />
          <DotLottieWorkerReact
            data={starAnim}
            autoplay
            loop
            width={126}
            height={126}
            className={classNames(styles.starAnim, styles.starAnimCenter)}
          />
          <LottieFromFrame
            data={starAnim}
            width={126}
            height={126}
            className={classNames(styles.starAnim, styles.starAnimRight)}
            frame={20}
          />
        </>
      )}
      <div className={classNames(styles.borderBox, {
        [styles.expand]: !items.length
      })}>
        <div className={styles.borderBoxInner}>
          <div className={styles.ratingTitle}>Rating</div>
          <div className={styles.ratingValue}>{rating}</div>
          {/* <DustedText
            withSpaces={false}
            type="words"
            className={styles.itemTitle}
            particleClassName={styles.itemTitleParticle}
            text={items[currentSlideIndex].title ?? ''}
            key={items[currentSlideIndex].title ?? ''}
          /> */}
          {/* <div className={styles.bottomValueWrapper}>
            <DustedText
              withSpaces={false}
              type="chars"
              className={styles.bottomValueContainer}
              particleClassName={styles.bottomValue}
              text={items[currentSlideIndex].value.toString()}
              key={items[currentSlideIndex].value}
            />
          </div> */}
        </div>
      </div>
      {items.length ? <div className={styles.swiperContainer}>
        <Swiper
          effect="coverflow"
          grabCursor
          centeredSlides
          loop
          slidesPerView="auto"
          onProgress={(swiper) => {
            swiper.slides.forEach((slideEl) => {
              const slide = slideEl as unknown as HTMLElement & {
                progress: number
              }
              const progress = slide.progress ?? 0
              const progressAbs = Math.abs(progress)

              let opacity = 1
              if (progressAbs >= 2.9) {
                opacity = 0
              } else if (progressAbs <= 1) {
                opacity = 1
              } else {
                opacity = 1 - unlerp(1, 2.9, progressAbs)
              }
              slide.style.opacity = opacity.toString()

              const currentCircle = slide.querySelector<HTMLElement>(
                '[data-slide-current-circle]'
              )
              if (currentCircle) {
                if (progressAbs >= 0 && progressAbs <= 0.7) {
                  const circleProgress = unlerp(0, 0.7, progressAbs)
                  currentCircle.style.opacity = (1 - circleProgress).toFixed(2)
                } else {
                  currentCircle.style.opacity = '0'
                }
              }
            })
          }}
          watchSlidesProgress
          onSlideChange={(swiper) => {
            setCurrentSlideIndex(swiper.realIndex)
          }}
          coverflowEffect={{
            rotate: 0,
            stretch: 150,
            depth: 50,
            modifier: 1,
            slideShadows: false,
            scale: 0.9,
          }}
          modules={[EffectCoverflow]}
          className={styles.swiper}
        >
          {items.map((item, i) => {
            const diff = getDiffFromCurrentSlide(
              currentSlideIndex,
              i,
              items.length
            )
            const key = item.id + i + (item.title ?? '')
            return (
              <SwiperSlide className={styles.slide} key={key}>
                <SlideItem item={item} diff={diff} />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div> : null}
    </div>
  )
}
